import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { disableNetwork, enableNetwork } from 'firebase/firestore';
import { requiresEmailVerification } from './authEmailValidation';
import { auth } from './firebase';
import { db } from './firebase';
import { createUserIfNew } from './userServices/userAccountLifecycleService';
import {
  clearActiveHomeSnapshot,
  clearHomeSnapshot,
  prepareHomeSnapshotAccount,
} from './app/homeSnapshotCache';
import { invalidateCachedRead } from './userServices/readCache';
import { syncCanonicalAnalyticsUser } from './analytics/firebaseAnalytics';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = still loading
  const activeUidRef = useRef(null);

  useEffect(() => {
    const transitionUserCache = (nextUid) => {
      const previousUid = activeUidRef.current;
      if (previousUid && previousUid !== nextUid) {
        clearHomeSnapshot(previousUid);
        invalidateCachedRead();
      }
      if (nextUid) {
        prepareHomeSnapshotAccount(nextUid);
      } else {
        clearActiveHomeSnapshot();
        invalidateCachedRead();
      }
      activeUidRef.current = nextUid || null;
    };

    let resolved = false;
    const fallback = setTimeout(() => {
      if (!resolved) {
        setUser(null);
      }
    }, 2500);

    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      resolved = true;
      clearTimeout(fallback);
      if (firebaseUser) {
        if (requiresEmailVerification(firebaseUser)) {
          syncCanonicalAnalyticsUser(null).catch(() => {});
          await signOut(auth).catch(() => {});
          disableNetwork(db).catch(() => {});
          transitionUserCache(null);
          setUser(null);
          return;
        }

        // Re-enable Firestore network (may have been disabled on previous sign-out)
        enableNetwork(db).catch(() => {});
        transitionUserCache(firebaseUser.uid);
        syncCanonicalAnalyticsUser(firebaseUser.uid).catch(() => {});
        setUser(firebaseUser);
        createUserIfNew(firebaseUser).catch(console.error);
        firebaseUser.reload()
          .then(async () => {
            if (!requiresEmailVerification(firebaseUser)) return;
            await signOut(auth).catch(() => {});
            syncCanonicalAnalyticsUser(null).catch(() => {});
            disableNetwork(db).catch(() => {});
            transitionUserCache(null);
            setUser(null);
          })
          .catch(() => {});
      } else {
        // Disable Firestore network BEFORE clearing user so in-flight
        // watch streams don't fire "Missing or insufficient permissions"
        disableNetwork(db).catch(() => {});
        transitionUserCache(null);
        syncCanonicalAnalyticsUser(null).catch(() => {});
        setUser(null);
      }
    }, () => {
      resolved = true;
      clearTimeout(fallback);
      transitionUserCache(null);
      syncCanonicalAnalyticsUser(null).catch(() => {});
      setUser(null);
    });

    return () => {
      clearTimeout(fallback);
      unsub();
    };
  }, []);

  return <AuthContext.Provider value={{ user }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
